import cx from 'classnames'
import type { CSSProperties } from 'react'

type Props = {
  className?: string
  spinnerDuration?: string
}

/**
 * A circular spinner component that can be used to indicate loading states in the UI. The spinner is created using CSS animations and can be customized with different sizes and colors by passing additional class names through the `className` prop.
 *
 * Note: vite.common.ts has a hardcoded variant of this until the app hydrates, so be mindful of making breaking changes to the structure of this component without updating the vite.common.ts implementation as well.
 */
export const CircularSpinner = ({
  className = 'size-8',
  spinnerDuration = '0.75s',
  ...restProps // Needed for e.g. tooltips
}: Props) => (
  <div
    className={cx(
      'relative overflow-hidden rounded-full inset-ring-2 inset-ring-current/10 text-primary',
      'animate-fade-in',
      className,
    )}
    role="status"
    data-testid="circularSpinner"
    style={{ '--spinner-duration': spinnerDuration } as CSSProperties}
    {...restProps}
  >
    <div
      className={cx(
        'absolute inset-0 mask-[linear-gradient(transparent_50%,white_50%),linear-gradient(to_right,transparent_50%,white_50%)] animate-[rotate-spinner_calc(var(--spinner-duration)*8)_infinite]',
        'before:content-[""] before:absolute before:inset-0 before:rounded-full before:border-2 before:border-solid before:border-transparent before:border-t-current before:bg-transparent before:animate-[rotate-spinner-before_var(--spinner-duration)_infinite_linear_alternate]',
      )}
    />
  </div>
)
